import { PERSONALITIES, type PersonalityId } from './ai/personality.js';
import type { SeatInput } from './state.js';
import { type PlayerId, playerId } from './types.js';

/**
 * Single-source-of-truth for a single seat in a configured game. Used
 * by the lobby (presentation/scenes/LobbyScene.ts) to capture the user's
 * choices and by GameScene to construct the initial state. Designed to
 * round-trip through scene data — fully serializable, no class
 * instances, no Phaser references.
 *
 * `personality` is a string id rather than the full AiPersonality
 * object so the config stays tiny and JSON-friendly. The lobby
 * surfaces only the three archetype ids; createInitialState's caller
 * (GameScene) resolves the id to the full object before constructing
 * the GameState.
 */
export interface SeatConfig {
  readonly id: PlayerId;
  readonly name: string;
  readonly color: string;
  readonly kind: 'human' | 'ai';
  /** 1..4. Same value on two seats = teammates. */
  readonly team: number;
  /** AI seats only. Unused on human seats. */
  readonly personality?: PersonalityId;
}

/**
 * Configured game ready for createInitialState. Carries the chosen
 * map id and the ordered list of seats. Seat order doubles as
 * `playerOrder` in the resulting GameState.
 */
export interface GameSetup {
  readonly mapId: string;
  readonly seats: readonly SeatConfig[];
}

// --- Defaults the lobby seeds the UI with on first visit ---

/** Color palette for the four configurable seats. */
export const SEAT_COLORS: readonly string[] = [
  '#8e2434', // Maroon
  '#294a78', // Navy
  '#2d6240', // Forest
  '#5e2c84', // Purple
];

/** Display name per seat color. */
export const SEAT_NAMES: readonly string[] = [
  'Maroon',
  'Navy',
  'Forest',
  'Purple',
];

/**
 * Default personalities for AI seats 1-3 (the three non-human seats
 * in a default 4-seat lobby). Picks for variety: each archetype
 * appears once so the human faces three different play styles by
 * default. The user can override any of these in the lobby.
 */
export const DEFAULT_AI_PERSONALITIES: readonly PersonalityId[] = [
  'aggressive',
  'cautious',
  'wildcard',
];

/**
 * Build a fresh default seat list of length `count`. Seat 1 is human
 * (the local player); seats 2-N are AI with the default
 * personalities above. Each seat is on its own team (FFA). Used by
 * the lobby on first open and by GameScene as a fallback.
 */
export function defaultSeats(count: number): readonly SeatConfig[] {
  const clamped = Math.max(2, Math.min(4, count));
  const seats: SeatConfig[] = [];
  for (let i = 0; i < clamped; i++) {
    const isHuman = i === 0;
    const base = {
      id: playerId(`p${i + 1}`),
      name: isHuman ? 'You' : SEAT_NAMES[i] ?? `P${i + 1}`,
      color: SEAT_COLORS[i] ?? '#888888',
      // FFA default — seat i is on team i+1 (1..4).
      team: i + 1,
    } as const;
    if (isHuman) {
      seats.push({ ...base, kind: 'human' });
    } else {
      const personality =
        DEFAULT_AI_PERSONALITIES[i - 1] ?? 'cautious';
      seats.push({ ...base, kind: 'ai', personality });
    }
  }
  return seats;
}

/**
 * Default game setup at boot — 3 seats on Ring, FFA. Used by GameScene
 * when no setup is passed via scene data (e.g., direct scene.start
 * calls or tests).
 */
export const DEFAULT_SETUP: GameSetup = {
  mapId: 'ring',
  seats: defaultSeats(3),
};

/**
 * True iff the configured seats represent a winnable game — at least
 * two distinct teams must be present, otherwise everyone is on the
 * same side and victory can never trigger. The lobby uses this to
 * gate the Start button.
 */
export function setupIsWinnable(setup: GameSetup): boolean {
  const teams = new Set(setup.seats.map((s) => s.team));
  return teams.size >= 2;
}

/**
 * Convert lobby-form seats (with string personality ids) into the
 * createInitialState input form (with full AiPersonality objects on
 * AI seats). The lookup against PERSONALITIES is total for the three
 * shipped archetypes; an unknown id leaves personality unset and the
 * AI falls back to DEFAULT_PERSONALITY at action time.
 */
export function seatsToInputs(
  seats: readonly SeatConfig[]
): readonly SeatInput[] {
  return seats.map((s) => {
    const base = {
      id: s.id,
      name: s.name,
      kind: s.kind,
      color: s.color,
      team: s.team,
    };
    if (s.kind === 'ai' && s.personality) {
      const obj = PERSONALITIES.find((p) => p.id === s.personality);
      return obj ? { ...base, personality: obj } : base;
    }
    return base;
  });
}
